// src/pages/master/UserManagement.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  Collapse,
} from "@mui/material";
import { styled } from "@mui/system";
import API from "../../api";
import { removeAuth } from "../../utils/auth";

const Container = styled(Box)({ padding: 16 });
const Content = styled(Paper)({
  padding: 16,
  minWidth: "100%",
  margin: "auto",
});

export default function UserManagement() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(null); // userId
  const [projects, setProjects] = useState({}); // map userId → projects[]
  const [projLoading, setProjLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/master/users");
        setUsers(data);
      } catch (err) {
        if (err.response?.status === 401) {
          removeAuth();
          window.location.href = "/login";
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleExpand = async (userId) => {
    if (expanded === userId) {
      // collapse
      setExpanded(null);
      return;
    }
    setProjLoading(true);
    try {
      const { data } = await API.get(`/master/users/${userId}/projects`);
      setProjects((prev) => ({ ...prev, [userId]: data }));
      setExpanded(userId);
    } catch (err) {
      console.error(err);
    } finally {
      setProjLoading(false);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Typography variant="h5" gutterBottom>
        User Management
      </Typography>
      <Button variant="contained" sx={{ mb: 2 }}>
        + Add User
      </Button>

      <TableContainer component={Content}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Super Admin</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((u) => (
              <React.Fragment key={u._id}>
                <TableRow hover onClick={() => handleExpand(u._id)}>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.mobile}</TableCell>
                  <TableCell>{u.createdBy?.name || "—"}</TableCell>
                  <TableCell align="right">
                    <Button size="small">Edit</Button>
                    <Button size="small" color="error">
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>

                {/* expandable */}
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell style={{ padding: 0 }} colSpan={8}>
                    <Collapse
                      in={expanded === u._id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box margin={2}>
                        <Typography variant="subtitle1" gutterBottom>
                          Projects for {u.name}
                        </Typography>
                        {projLoading && expanded === u._id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Project Name</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {projects[u._id]?.map((p) => (
                                <TableRow key={p._id}>
                                  <TableCell>
                                    {new Date(p.createdAt).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>{p.companyName}</TableCell>
                                  <TableCell>{p.plan.name}</TableCell>
                                  <TableCell align="right">
                                    {p.planPrice}
                                  </TableCell>
                                </TableRow>
                              ))}

                              {/* total row */}
                              <TableRow>
                                <TableCell colSpan={3} align="right">
                                  <strong>Total:</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>
                                    {projects[u._id]?.reduce(
                                      (sum, p) => sum + (p.planPrice || 0),
                                      0
                                    )}
                                  </strong>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
